import { Injectable, Logger } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';

export type AlertKind =
  | 'wal_lag'
  | 'wal_gap'
  | 'run_failed'
  | 'sla_breach'
  | 'repo_unreachable'
  | 'drill_failed'
  | 'disk_low'
  | 'verify_failed'
  | 'bloat_high'
  | 'xid_wraparound'
  | 'quota_soft'
  | 'quota_hard'
  | 'archive_backlog'
  | 'window_coverage'
  | 'policy_unsupported'
  | 'wal_archiving_off';

export interface RaiseAlertInput {
  kind: AlertKind;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  targetId?: string | null;
  detail?: Record<string, unknown>;
}

/**
 * Raise, dedupe and resolve backup alerts.
 *
 * THE DEDUPE IS THE POINT. A failing `archive_command` is detected every two
 * minutes, and an alert system that inserts a row each time produces 720 rows a
 * day for one problem. The inbox then becomes unreadable exactly when it
 * matters most, which is the failure mode that makes people stop looking at
 * alerts altogether.
 *
 * So one OPEN row per (kind, targetId), with `occurrences` and `lastSeenAt`
 * carrying the repetition. A partial unique index in migration 057 enforces
 * this at the storage layer too, because a race between two schedulers would
 * otherwise slip a duplicate past the application check.
 */
@Injectable()
export class BackupAlertService {
  private readonly logger = new Logger(BackupAlertService.name);

  constructor(private readonly prisma: PrismaService) {}

  async raise(input: RaiseAlertInput): Promise<void> {
    // '' and NULL are the SAME key to the partial unique index
    // (`COALESCE("targetId", '')`), but NOT to a Prisma query: `targetId: null`
    // compiles to `targetId IS NULL`, which does not match a row holding ''.
    // Normalising here is what keeps the application's idea of "no target" and
    // the index's idea of it identical.
    const targetId = input.targetId?.trim() ? input.targetId : null;

    if (await this.foldIntoExisting(input, targetId)) return;

    try {
      await this.prisma.backupAlert.create({
        data: {
          kind: input.kind,
          targetId,
          severity: input.severity,
          title: input.title,
          detail: (input.detail ?? {}) as never,
          status: 'open',
        },
      });

      this.logger.warn(`[${input.severity}] ${input.kind}: ${input.title}`);
    } catch (err) {
      // Find-then-insert is not atomic, and the index enforces a rule the
      // preceding query only approximates. A P2002 here therefore means the
      // alert already exists — which is precisely the state raise() wants.
      //
      // This crashed the hourly SLA check in production every hour for days:
      // the exception escaped raise(), escaped checkSla(), and aborted the loop
      // before the remaining policies were evaluated. An alert that takes down
      // the detector raising it is worse than no alert.
      if ((err as { code?: string }).code !== 'P2002') throw err;

      if (!(await this.foldIntoExisting(input, targetId))) {
        this.logger.error(
          `${input.kind}: duplicate alert row is present but not visible to the dedupe query ` +
            `(targetId=${JSON.stringify(targetId)}). Left as-is rather than failing the caller.`
        );
      }
    }
  }

  /**
   * Update the open alert this one repeats, if there is one.
   *
   * Returns false when there is nothing to fold into. Matches the index's key
   * semantics rather than Prisma's: with no target, a legacy row holding ''
   * and a row holding NULL are the same alert, and treating them as two is
   * what produced the duplicate-key crash in the first place.
   */
  private async foldIntoExisting(input: RaiseAlertInput, targetId: string | null): Promise<boolean> {
    const key = targetId === null ? { OR: [{ targetId: null }, { targetId: '' }] } : { targetId };

    const existing = await this.prisma.backupAlert.findFirst({
      where: { kind: input.kind, status: { not: 'resolved' }, ...key },
    });

    if (!existing) return false;

    await this.prisma.backupAlert.update({
      where: { id: existing.id },
      data: {
        lastSeenAt: new Date(),
        occurrences: { increment: 1 },
        // Severity can escalate but never quietly de-escalate: a condition
        // that was critical and is now merely warning still needs the
        // operator who was paged to close it deliberately.
        severity: this.escalate(existing.severity, input.severity),
        title: input.title,
        detail: (input.detail ?? {}) as never,
        // An acknowledged alert that recurs is re-opened. Acknowledgement
        // means "I have seen this", not "stop telling me".
        status: existing.status === 'acknowledged' ? 'open' : existing.status,
      },
    });

    return true;
  }

  /**
   * Close any open alert of this kind.
   *
   * Called by the detector that raised it, once the condition is genuinely
   * gone. Auto-resolution is safe here — and preferable to leaving stale rows —
   * because every one of these conditions is continuously re-evaluated, so a
   * problem that comes back re-raises within minutes.
   */
  async resolve(kind: AlertKind, targetId: string | null): Promise<void> {
    // Same key semantics as raise(), for the same reason: an alert stored with
    // '' must still be closable by a caller that says null.
    const key = targetId?.trim() ? { targetId } : { OR: [{ targetId: null }, { targetId: '' }] };

    await this.prisma.backupAlert.updateMany({
      where: { kind, status: { not: 'resolved' }, ...key },
      data: { status: 'resolved', resolvedAt: new Date() },
    });
  }

  async acknowledge(id: string, actorId: string): Promise<void> {
    await this.prisma.backupAlert.update({
      where: { id },
      data: { status: 'acknowledged', acknowledgedBy: actorId, acknowledgedAt: new Date() },
    });
  }

  async open(): Promise<unknown[]> {
    return this.prisma.backupAlert.findMany({
      where: { status: { not: 'resolved' } },
      orderBy: [{ severity: 'asc' }, { lastSeenAt: 'desc' }],
      take: 100,
    });
  }

  private escalate(current: string, incoming: string): string {
    const rank: Record<string, number> = { info: 0, warning: 1, critical: 2 };
    return (rank[incoming] ?? 0) > (rank[current] ?? 0) ? incoming : current;
  }
}
